import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Lock, ShieldAlert, Loader2 } from 'lucide-react';
import { API } from '../lib/api';

// Passcode gate for the hidden admin dashboard. Verifies against the backend
// (bcrypt-hashed passcode in .env) and returns a short-lived JWT to the parent
// on success. The passcode is NEVER stored in the frontend bundle.
const AdminLoginModal = ({ open, onClose, onAuthed }) => {
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPasscode('');
      setError(null);
      setBusy(false);
      // Autofocus once the modal is on-screen
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!passcode) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { passcode });
      onAuthed(data.token, data.expires_in);
    } catch (err) {
      setError('❌ Wrong passcode! Try again.');
      setPasscode('');
      setTimeout(() => inputRef.current?.focus(), 20);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="admin-login-modal"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-xl"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-3xl bg-gradient-to-br from-[#0b0f1a] to-[#12172a] border border-white/10 p-6 shadow-2xl"
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#7DF9FF]/20 to-[#A78BFA]/20 border border-white/10 flex items-center justify-center">
            <Lock className="w-5 h-5 text-[#7DF9FF]" />
          </div>
          <div>
            <p className="font-display font-semibold text-white text-lg leading-tight">Restricted area</p>
            <p className="text-xs text-forge-muted">Enter passcode to unlock</p>
          </div>
        </div>

        <label className="block text-xs uppercase tracking-widest text-forge-muted mb-2">Passcode</label>
        <input
          ref={inputRef}
          type="password"
          value={passcode}
          onChange={(e) => setPasscode(e.target.value)}
          data-testid="admin-passcode-input"
          autoComplete="off"
          spellCheck={false}
          className="cf-input font-code"
          placeholder="••••••••"
          disabled={busy}
        />

        {error && (
          <p data-testid="admin-passcode-error" className="mt-3 text-sm text-[#FF6B7A] flex items-center gap-1.5">
            <ShieldAlert className="w-4 h-4" /> {error}
          </p>
        )}

        <div className="flex gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="cf-btn-secondary text-sm flex-1 justify-center"
            data-testid="admin-login-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !passcode}
            className="cf-btn-primary text-sm flex-1 justify-center"
            data-testid="admin-login-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {busy ? 'Verifying…' : 'Unlock'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminLoginModal;
